import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { OUTLINED_SX } from '../../Benchmarks/components/PageHeader';
import { GROWTH } from '../tokens';
import { SKILL_LABELS, SKILLS, type Skill } from '../types';

export function SkillTabs({ value, onChange }: { value: Skill; onChange: (skill: Skill) => void }) {
  return (
    <Box role="group" aria-label="Skill" sx={{ display: 'flex', gap: '4px', p: '4px', bgcolor: GROWTH.track, borderRadius: '8px' }}>
      {SKILLS.map((skill) => {
        const pressed = skill === value;
        return (
          <Box
            key={skill}
            component="button"
            type="button"
            aria-pressed={pressed}
            onClick={() => onChange(skill)}
            sx={{
              fontFamily: GROWTH.font,
              fontSize: '16px',
              lineHeight: '24px',
              fontWeight: pressed ? 700 : 600,
              p: '4px 12px',
              border: 0,
              borderRadius: '4px',
              bgcolor: pressed ? GROWTH.cardBg : 'transparent',
              color: pressed ? GROWTH.ink : GROWTH.muted,
              boxShadow: pressed ? '0px 1px 3px rgba(0,0,0,0.12)' : 'none',
              cursor: 'pointer',
            }}
          >
            {SKILL_LABELS[skill]}
          </Box>
        );
      })}
    </Box>
  );
}

const SELECT_SX = {
  fontFamily: GROWTH.font,
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 700,
  color: GROWTH.ink,
  bgcolor: GROWTH.cardBg,
  borderRadius: '8px',
  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: '8px', p: '8px 12px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: GROWTH.divider },
} as const;

function Eyebrow({ children }: { children: string }) {
  return (
    <Typography component="small" sx={{ fontFamily: GROWTH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 700, letterSpacing: '0.06em', color: GROWTH.muted, textTransform: 'uppercase' }}>
      {children}
    </Typography>
  );
}

export interface SelectOption<T extends string | number> {
  value: T;
  label: string;
}

export function LabelledSelect<T extends string | number>({ label, value, options, onChange }: { label: string; value: T; options: SelectOption<T>[]; onChange: (value: T) => void }) {
  return (
    <Select
      size="small"
      value={value}
      disabled={options.length < 2 && options.some((option) => option.value === value)}
      inputProps={{ 'aria-label': label }}
      onChange={(event) => {
        const picked = options.find((option) => String(option.value) === String(event.target.value));
        if (picked) onChange(picked.value);
      }}
      renderValue={(selected) => (
        <>
          <Eyebrow>{label}</Eyebrow>
          {options.find((option) => option.value === selected)?.label ?? ''}
        </>
      )}
      sx={SELECT_SX}
      MenuProps={{ PaperProps: { sx: { minWidth: 200 } } }}
    >
      {options.map((option) => (
        <MenuItem key={String(option.value)} value={option.value} sx={{ fontFamily: GROWTH.font, fontSize: 16 }}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
}

export function yearLabel(schoolYear: number): string {
  return `${schoolYear - 1}–${String(schoolYear).padStart(2, '0').slice(-2)}`;
}

export function ActionButton({ label, onClick, fill }: { label: string; onClick: () => void; fill?: boolean }) {
  return (
    <Button
      variant={fill ? 'contained' : 'outlined'}
      disableRipple
      onClick={onClick}
      sx={fill ? { ...OUTLINED_SX, color: GROWTH.cardBg, bgcolor: GROWTH.action, '&:hover': { bgcolor: GROWTH.action } } : OUTLINED_SX}
    >
      {label}
    </Button>
  );
}

export function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      data-testid="growth-filter-chip"
      aria-label={`Remove the ${label} filter`}
      onClick={onRemove}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        p: '8px 12px',
        borderRadius: '9999px',
        border: 0,
        bgcolor: GROWTH.actionSoft,
        color: GROWTH.action,
        fontFamily: GROWTH.font,
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        '&:hover': { bgcolor: GROWTH.actionSofter },
      }}
    >
      {label}
      <span aria-hidden="true">✕</span>
    </Box>
  );
}

export function ClassChip({ label, pressed, onClick }: { label: string; pressed: boolean; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      sx={{
        p: '4px 16px',
        border: `1px solid ${pressed ? GROWTH.action : GROWTH.divider}`,
        borderRadius: '9999px',
        bgcolor: pressed ? GROWTH.action : GROWTH.cardBg,
        color: pressed ? GROWTH.cardBg : GROWTH.ink,
        fontFamily: GROWTH.font,
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: pressed ? 700 : 600,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
    >
      {label}
    </Box>
  );
}

export function SearchBox({ value, onChange, label }: { value: string; onChange: (value: string) => void; label: string }) {
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        p: '8px 12px',
        border: `1px solid ${GROWTH.divider}`,
        borderRadius: '8px',
        bgcolor: GROWTH.cardBg,
        width: 200,
        boxSizing: 'border-box',
        '&:focus-within': { borderColor: GROWTH.action },
      }}
    >
      <SearchIcon sx={{ fontSize: 16, color: GROWTH.muted }} />
      <Box
        component="input"
        type="search"
        aria-label={label}
        placeholder={label}
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        sx={{ border: 0, outline: 0, background: 'none', width: '100%', fontFamily: GROWTH.font, fontSize: '16px', lineHeight: '24px', color: GROWTH.ink }}
      />
    </Box>
  );
}

export function SwitchButton({ label, pressed, onChange }: { label: string; pressed: boolean; onChange: (pressed: boolean) => void }) {
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={pressed}
      onClick={() => onChange(!pressed)}
      sx={{ display: 'flex', alignItems: 'center', gap: '8px', border: 0, background: 'none', p: 0, fontFamily: GROWTH.font, fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: GROWTH.ink, whiteSpace: 'nowrap', cursor: 'pointer' }}
    >
      <Box
        component="i"
        aria-hidden="true"
        sx={{
          width: 36,
          height: 20,
          borderRadius: '9999px',
          bgcolor: pressed ? GROWTH.action : GROWTH.moveBorder.same,
          position: 'relative',
          flexShrink: 0,
          '&::after': { content: '""', position: 'absolute', top: 3, left: pressed ? 19 : 3, width: 14, height: 14, borderRadius: '9999px', bgcolor: GROWTH.cardBg },
        }}
      />
      {label}
    </Box>
  );
}
